// Packet coalescing — the transport-agnostic "sandwich".
//
// Many small messages ride the same tick: on a client, every script on the
// shared bus calls send() once a frame; on the server, every relay fans a
// subject out per tick. Emitting one datagram per message wastes the
// per-datagram overhead (transport headers, framing, dispatch) that dominates
// a tens-of-bytes game packet. Instead the host BUFFERS every frame produced
// during a tick and flushes them as one — or, past the MTU, a few — coalesced
// datagrams. The receiver splits the sandwich back into the EXACT per-frame
// buffers the un-coalesced path produced, so nothing downstream — least of all
// a Munos script — can tell the difference (docs/coalescing).
//
// This layer is deliberately transport-agnostic: it operates on byte arrays
// only, with no WebSocket/UDP assumptions, so a future transport reuses it
// verbatim. Reliability classes are handled by the CALLER (one buffer per
// class), not here — the codec just packs opaque frames.
//
// Wire format (little-endian). A single leading tag byte makes the datagram
// self-describing, so a future transport or implementation can dispatch on the
// envelope rather than relying on any transport-specific behavior:
//
//   datagram := [u8 tag=0x01] [record]*
//   record   := [u16 frameLen] [frame: frameLen bytes]
//   frame    := the existing channel-stamped frame ([u16 channel] [body]),
//               byte-identical to a single un-coalesced send.
//
// The frame is opaque to this codec — it is length-prefixed and copied through
// untouched, so the channel demux downstream is entirely unchanged.
// Envelope format tag. Byte 0 of every coalesced datagram. A receiver that
// reads a different leading byte is looking at a foreign/legacy frame and
// drops it rather than misparsing.
export const ENVELOPE_TAG = 0x01;
// Fixed overheads, in bytes: the leading tag, and the u16 length that prefixes
// each record.
const ENVELOPE_HEADER = 1;
const RECORD_HEADER = 2;
// The u16 length field caps a single framed record's body. No game packet
// approaches this (they run tens of bytes); a frame past it is a programming
// error, surfaced by encodeEnvelope rather than silently truncated.
const MAX_FRAME_LEN = 0xffff;
// Default per-datagram size budget. 1200 bytes stays under the smallest
// realistic path MTU (safe for a future UDP/P2P transport); an ordered
// stream transport can afford more but this default never hurts. The cap is a
// guard, not the common path — real traffic is one small datagram.
export const DEFAULT_MAX_DATAGRAM = 1200;
// Pack a tick's worth of frames into coalesced datagrams, each at most
// `maxBytes`. Frames are packed greedily in order; a frame is NEVER split
// across two datagrams (partial loss of an unreliable datagram must never
// yield an unreassemblable fragment). A frame whose record cannot fit in a
// fresh datagram alone still ships alone — we do not fragment. An empty input
// yields no datagrams (an empty flush sends nothing).
export function encodeEnvelope(frames, maxBytes = DEFAULT_MAX_DATAGRAM) {
    const out = [];
    let batch = [];
    let batchSize = ENVELOPE_HEADER;
    const flush = () => {
        if (batch.length === 0)
            return;
        out.push(buildDatagram(batch, batchSize));
        batch = [];
        batchSize = ENVELOPE_HEADER;
    };
    for (const frame of frames) {
        if (frame.length > MAX_FRAME_LEN) {
            throw new Error(`envelope: frame of ${frame.length} bytes exceeds the ${MAX_FRAME_LEN}-byte record limit`);
        }
        const recordSize = RECORD_HEADER + frame.length;
        // Start a new datagram only when the current one already holds something
        // AND this record would overflow it. If the batch is empty we add the
        // record regardless — that is the "oversize ships alone" path.
        if (batch.length > 0 && batchSize + recordSize > maxBytes)
            flush();
        batch.push(frame);
        batchSize += recordSize;
    }
    flush();
    return out;
}
function buildDatagram(frames, totalSize) {
    const buf = new Uint8Array(totalSize);
    buf[0] = ENVELOPE_TAG;
    let off = ENVELOPE_HEADER;
    for (const frame of frames) {
        const len = frame.length;
        buf[off] = len & 0xff;
        buf[off + 1] = (len >> 8) & 0xff;
        off += RECORD_HEADER;
        buf.set(frame, off);
        off += len;
    }
    return buf;
}
// Split a coalesced datagram back into its frames, each byte-identical to what
// encodeEnvelope was handed. Returns views (subarrays) into the datagram — the
// caller dispatches them synchronously, before the buffer can change. Every
// length read is bounds-checked: a wrong tag yields no frames, and a truncated
// or malformed record stops the walk at the last whole frame rather than
// reading past the buffer.
export function decodeEnvelope(datagram) {
    const frames = [];
    if (datagram.length < ENVELOPE_HEADER || datagram[0] !== ENVELOPE_TAG)
        return frames;
    let off = ENVELOPE_HEADER;
    while (off + RECORD_HEADER <= datagram.length) {
        const len = datagram[off] | (datagram[off + 1] << 8);
        off += RECORD_HEADER;
        if (off + len > datagram.length)
            break; // truncated record — stop safely
        frames.push(datagram.subarray(off, off + len));
        off += len;
    }
    return frames;
}
//# sourceMappingURL=envelope.js.map